import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CheckCircle, Building2 } from 'lucide-react';

export default function LandingPricing({ onRegisterClick, onEnterpriseClick }) {
  const { t } = useTranslation();
  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">{t('landing.pricing.title')}</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">{t('landing.pricing.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Free Plan */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('landing.pricing.free.name')}</h3>
              <div className="text-5xl font-bold text-slate-900 mb-2">{t('landing.pricing.free.price')}</div>
              <p className="text-slate-600">{t('landing.pricing.free.period')}</p>
            </div>
            <ul className="space-y-4 mb-8">
              {[1,2,3,4].map((n) => {
                const feat = t(`landing.pricing.free.feature${n}`);
                return (
                  <li key={feat} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700">{feat}</span>
                  </li>
                );
              })}
            </ul>
            <Button className="w-full" variant="outline" onClick={onRegisterClick} data-testid="pricing-free-cta">
              {t('landing.pricing.free.cta')}
            </Button>
          </div>
          {/* Pay-as-you-go Plan */}
          <div className="bg-primary rounded-2xl p-8 shadow-2xl border-2 border-primary transform scale-105 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-slate-900 px-4 py-1 rounded-full text-sm font-bold">
              {t('landing.pricing.payg.badge')}
            </div>
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">{t('landing.pricing.payg.name')}</h3>
              <div className="text-5xl font-bold text-white mb-2">{t('landing.pricing.payg.price')}</div>
              <p className="text-primary-foreground/80">{t('landing.pricing.payg.period')}</p>
            </div>
            <ul className="space-y-4 mb-8">
              {[1,2,3,4,5].map((n) => {
                const feat = t(`landing.pricing.payg.feature${n}`);
                return (
                  <li key={feat} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                    <span className="text-white">{feat}</span>
                  </li>
                );
              })}
            </ul>
            <Button className="w-full bg-white text-primary hover:bg-secondary" onClick={onRegisterClick} data-testid="pricing-payg-cta">
              {t('landing.pricing.payg.cta')}
            </Button>
          </div>
          {/* Consultant Plan */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('landing.pricing.consultant.name')}</h3>
              <div className="text-5xl font-bold text-slate-900 mb-2">{t('landing.pricing.consultant.price')}</div>
              <p className="text-slate-600">{t('landing.pricing.consultant.period')}</p>
            </div>
            <ul className="space-y-4 mb-8">
              {[1,2,3,4,5].map((n) => {
                const feat = t(`landing.pricing.consultant.feature${n}`);
                return (
                  <li key={feat} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700">{feat}</span>
                  </li>
                );
              })}
            </ul>
            <Button className="w-full" variant="outline" onClick={onRegisterClick} data-testid="pricing-consultant-cta">
              {t('landing.pricing.consultant.cta')}
            </Button>
          </div>
        </div>
        <p className="text-center text-slate-600 mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <span>{t('landing.pricing.enterpriseLabel') || 'Need more? Talk to our team about Enterprise pricing.'}</span>
          <button
            onClick={onEnterpriseClick}
            data-testid="enterprise-contact-btn"
            className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-slate-800 transition whitespace-nowrap"
          >
            <Building2 className="w-4 h-4" />
            {t('landing.pricing.enterpriseCta') || 'Contact Us'}
          </button>
        </p>
      </div>
    </section>
  );
}
